// Errors — application failures read live from the function logs, and the
// deploy reports the pipeline wrote. Admin only (owner, 2026-09-14;
// docs/plans/2026-09-14-admin-errors-page.md §7).
//
// TWO TABS, in the header (owner, 2026-09-28; they were two stacked sections
// with a title each until the page titles went). "Application Errors" runs `GET /ops/errors` for the chosen
// window and shows the rows the server classified — worker failures, API
// server errors and, behind a switch, the Bedrock retries and fallbacks. The
// server answers `running` when Logs Insights has not finished within its
// wait, and this screen polls with the query id until it has (1.5 s apart,
// bounded). "Deploys" lists `GET /ops/deploys`, one row per build, expanding
// to the report text exactly as it was mailed.
//
// THE NOISE CONTROLS ARE THE POINT (owner: "make it easy to filter out any
// noise"). Repeats collapse into one row with a count by default; warnings are
// off by default; kind and function narrow the list; free text matches the
// identifying fields; and "Mute Event" hides an event NAME for this viewer,
// remembered in this browser only (`lib/errorsView.ts`, E-5). Mutes are also
// sent to the server as `exclude_events`, so a muted burst is not even fetched.
//
// NOTHING HERE IS PHI. A row carries the allow-listed names the server copied
// and nothing else; the deploy report is build output. This screen adds no field.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dropdown,
  Input,
  Option,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { ArrowClockwiseRegular, ChevronDownRegular, ChevronRightRegular, DismissRegular, SpeakerMuteRegular } from '@fluentui/react-icons'
import { getClient } from '../api/client'
import { errorMessage } from '../api/errors'
import type { DeployReport, OpsErrorKind, OpsErrorRow, OpsErrorsWindow } from '../api/types'
import { ErrorBanner } from '../components/States'
import { MultiSelectField } from '../components/MultiSelectField'
import { PageTabs } from '../components/PageTabs'
import { ScreenIntro } from '../components/ScreenIntro'
import { applyMutes, groupRows, matchesText, readMutes, writeMutes, type ErrorGroup } from '../lib/errorsView'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  // The page's column gap already spaces the intro from what follows.
  intro: { marginBottom: 0 },
  headerSub: { color: tokens.colorNeutralForeground3 },
  controls: { display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' },
  control: { display: 'flex', flexDirection: 'column', gap: '4px' },
  // The multi-select fills its container, so the container sets the width.
  functionControl: { width: '260px' },
  kinds: { display: 'flex', gap: '6px', alignItems: 'center' },
  mutes: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  status: { display: 'flex', alignItems: 'center', gap: '8px', color: tokens.colorNeutralForeground3 },
  table: { tableLayout: 'fixed' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  detailRow: { backgroundColor: tokens.colorNeutralBackground2 },
  detailList: { display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '12px', rowGap: '2px', padding: '6px 0' },
  report: {
    whiteSpace: 'pre-wrap',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    margin: 0,
    padding: '8px 0',
  },
  toggle: { minWidth: 'auto', padding: 0 },
  /** The muted-event chip. See the comment at its render for why this is not a
      Fluent `Badge`. */
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    padding: '1px 2px 1px 8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusCircular,
    color: tokens.colorNeutralForeground2,
  },
  count: { fontVariantNumeric: 'tabular-nums' },
})

type ErrorsView = 'errors' | 'deploys'
const ERRORS_TABS: { value: ErrorsView; label: string }[] = [
  { value: 'errors', label: 'Application Errors' },
  { value: 'deploys', label: 'Deploys' },
]

const WINDOWS: { value: OpsErrorsWindow; label: string }[] = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

const KIND_LABEL: Record<OpsErrorKind, string> = { worker: 'Worker', api: 'API', bedrock: 'Bedrock' }
const ALL_KINDS: OpsErrorKind[] = ['worker', 'api', 'bedrock']

/** How long to keep polling a `running` query before giving up and offering Refresh. */
const POLL_MS = 1500
const POLL_MAX = 20

type LoadState =
  | { status: 'loading' | 'running' }
  | { status: 'ready'; rows: OpsErrorRow[]; truncated: boolean; scannedBytes: number }
  | { status: 'failed'; message: string }

const when = (iso: string): string => new Date(iso).toLocaleString()

function storage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    return undefined
  }
}

export function ErrorsScreen() {
  const s = useStyles()
  // Which tab. Both reads still start when the page opens, so switching tabs
  // shows what is already loaded rather than starting a read.
  const [view, setView] = useState<ErrorsView>('errors')
  const [window_, setWindow] = useState<OpsErrorsWindow>('7d')
  const [kinds, setKinds] = useState<Set<OpsErrorKind>>(new Set(ALL_KINDS))
  const [includeWarnings, setIncludeWarnings] = useState(false)
  const [collapse, setCollapse] = useState(true)
  // Functions picked; empty shows every function (multi-select, owner 2026-10-14).
  const [fn, setFn] = useState<string[]>([])
  const [text, setText] = useState('')
  const [muted, setMuted] = useState<Set<string>>(() => readMutes(storage()))
  const [errors, setErrors] = useState<LoadState>({ status: 'loading' })
  const [deploys, setDeploys] = useState<{ status: 'loading' } | { status: 'ready'; data: DeployReport[] } | { status: 'failed'; message: string }>({ status: 'loading' })
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())
  const [openDeploys, setOpenDeploys] = useState<Set<string>>(new Set())
  const [nonce, setNonce] = useState(0)
  // A load in flight is identified by a token; a newer load or an unmount
  // makes the older one's answers fall on the floor.
  const inFlight = useRef(0)

  const load = useCallback(async () => {
    const token = ++inFlight.current
    setErrors({ status: 'loading' })
    const client = getClient()
    const params = {
      window: window_,
      kinds: [...kinds],
      includeWarnings,
      excludeEvents: [...muted],
      q: text.trim() === '' ? undefined : text.trim(),
    }
    try {
      let answer = await client.listOpsErrors(params)
      let polls = 0
      while (answer.status === 'running') {
        if (token !== inFlight.current) return
        if (polls >= POLL_MAX) {
          setErrors({ status: 'failed', message: 'The log query is still running. Refresh to try again.' })
          return
        }
        setErrors({ status: 'running' })
        await new Promise((r) => setTimeout(r, POLL_MS))
        polls += 1
        answer = await client.listOpsErrors({ ...params, queryId: answer.query_id })
      }
      if (token !== inFlight.current) return
      setErrors({ status: 'ready', rows: answer.rows, truncated: answer.truncated, scannedBytes: answer.scanned_bytes })
    } catch (e) {
      if (token !== inFlight.current) return
      setErrors({ status: 'failed', message: errorMessage(e) })
    }
    // Deliberately: `text` and `muted` are read when Refresh (or a control) fires
    // the load, so typing does not query the logs on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window_, kinds, includeWarnings, nonce])

  useEffect(() => {
    void load()
    return () => {
      inFlight.current += 1
    }
  }, [load])

  useEffect(() => {
    let cancelled = false
    getClient()
      .listDeployReports()
      .then((data) => {
        if (!cancelled) setDeploys({ status: 'ready', data })
      })
      .catch((e: unknown) => {
        if (!cancelled) setDeploys({ status: 'failed', message: errorMessage(e) })
      })
    return () => {
      cancelled = true
    }
  }, [nonce])

  const mute = (event: string) => {
    const next = new Set(muted)
    next.add(event)
    setMuted(next)
    writeMutes(storage(), next)
  }
  const unmute = (event: string) => {
    const next = new Set(muted)
    next.delete(event)
    setMuted(next)
    writeMutes(storage(), next)
    setNonce((n) => n + 1)
  }
  const toggleKind = (kind: OpsErrorKind) => {
    const next = new Set(kinds)
    if (next.has(kind)) next.delete(kind)
    else next.add(kind)
    if (next.size === 0) return
    setKinds(next)
  }

  const visibleRows = useMemo(() => {
    if (errors.status !== 'ready') return []
    return applyMutes(errors.rows, muted)
      .filter((r) => fn.length === 0 || fn.includes(r.function))
      .filter((r) => matchesText(r, text))
  }, [errors, muted, fn, text])

  const functions = useMemo(
    () => (errors.status === 'ready' ? [...new Set(errors.rows.map((r) => r.function))].sort() : []),
    [errors],
  )
  const functionOptions = useMemo(() => functions.map((f) => ({ value: f, label: f })), [functions])
  const groups: ErrorGroup[] = useMemo(() => groupRows(visibleRows), [visibleRows])

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const toggleDeploy = (id: string) =>
    setOpenDeploys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className={s.page}>
      <PageTabs
        tabs={ERRORS_TABS}
        selectedValue={view}
        onSelect={setView}
        ariaLabel="Errors sections"
      />
      {view === 'errors' && (
        <>
          <ScreenIntro className={s.intro}>What the system could not do, read from its own logs.</ScreenIntro>

          <div className={s.controls}>
            <div className={s.control}>
              <Caption1>Window</Caption1>
              <Dropdown
                aria-label="Window"
                value={WINDOWS.find((w) => w.value === window_)?.label}
                selectedOptions={[window_]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue === '24h' || d.optionValue === '7d' || d.optionValue === '30d') setWindow(d.optionValue)
                }}
              >
                {WINDOWS.map((w) => (
                  <Option key={w.value} value={w.value}>
                    {w.label}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div className={s.control}>
              <Caption1>Kind</Caption1>
              <div className={s.kinds} role="group" aria-label="Kinds">
                {ALL_KINDS.map((k) => (
                  <Button
                    key={k}
                    size="small"
                    appearance={kinds.has(k) ? 'primary' : 'secondary'}
                    aria-pressed={kinds.has(k)}
                    onClick={() => toggleKind(k)}
                    disabled={k === 'bedrock' && !includeWarnings}
                  >
                    {KIND_LABEL[k]}
                  </Button>
                ))}
              </div>
            </div>
            <Switch
              label="Include Warnings (Bedrock Retries and Fallbacks)"
              checked={includeWarnings}
              onChange={(_, d) => setIncludeWarnings(d.checked)}
            />
            <Switch label="Collapse Repeats" checked={collapse} onChange={(_, d) => setCollapse(d.checked)} />
            <div className={mergeClasses(s.control, s.functionControl)}>
              <Caption1>Function</Caption1>
              <MultiSelectField
                label=""
                ariaLabel="Function"
                placeholder="All functions"
                options={functionOptions}
                selected={fn}
                onChange={setFn}
              />
            </div>
            <div className={s.control}>
              <Caption1>Find</Caption1>
              <Input
                aria-label="Find"
                placeholder="event, error class or id…"
                value={text}
                onChange={(_, d) => setText(d.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setNonce((n) => n + 1)
                }}
              />
            </div>
            <Button icon={<ArrowClockwiseRegular />} onClick={() => setNonce((n) => n + 1)}>
              Refresh
            </Button>
          </div>

          {muted.size > 0 && (
            <div className={s.mutes}>
              <Caption1>Muted for you:</Caption1>
              {/* A HAND-ROLLED CHIP, NOT A FLUENT `Badge` (QA sweep finding B2-01,
                  2026-09-22). `Badge` draws its outline with an `::after` that is
                  absolutely positioned over the WHOLE badge — that is how the border
                  survives high contrast — and an absolutely-positioned pseudo-element
                  paints above a non-positioned child, so it swallowed every click
                  aimed at the dismiss button nested inside it. The chip rendered, the
                  X rendered, and `Unmute` simply never fired: a mute written to
                  `localStorage` could not be taken back through the app at all, on the
                  one screen that reports what the system could not do.

                  This is the only place in the app that puts a control inside a chip,
                  which is why nothing else showed it. Keep the button a SIBLING of the
                  label inside a plain element — no pseudo-element, nothing overlaying
                  the hit area. */}
              {[...muted].sort().map((event) => (
                <span key={event} className={s.chip}>
                  <span className={s.mono}>{event}</span>
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label={`Unmute ${event}`}
                    className={s.toggle}
                    onClick={() => unmute(event)}
                  />
                </span>
              ))}
            </div>
          )}

          {errors.status === 'failed' && <ErrorBanner message={errors.message} />}
          {(errors.status === 'loading' || errors.status === 'running') && (
            <div className={s.status} role="status">
              <Spinner size="tiny" />
              <Caption1>{errors.status === 'running' ? 'The log query is running…' : 'Reading the logs…'}</Caption1>
            </div>
          )}
          {errors.status === 'ready' && (
            <Caption1 className={s.headerSub}>
              {visibleRows.length} {visibleRows.length === 1 ? 'event' : 'events'}
              {collapse ? ` in ${groups.length} ${groups.length === 1 ? 'group' : 'groups'}` : ''}
              {errors.truncated ? ' — the query hit its 1,000-row limit; narrow the window' : ''}
            </Caption1>
          )}

          {errors.status === 'ready' && (
            <Table size="small" aria-label="Application errors" className={s.table}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell style={{ width: '32px' }} />
                  <TableHeaderCell style={{ width: '170px' }}>{collapse ? 'Last Seen' : 'When'}</TableHeaderCell>
                  {collapse && <TableHeaderCell style={{ width: '64px' }}>Count</TableHeaderCell>}
                  <TableHeaderCell style={{ width: '110px' }}>Function</TableHeaderCell>
                  <TableHeaderCell>Event</TableHeaderCell>
                  <TableHeaderCell>Error</TableHeaderCell>
                  <TableHeaderCell style={{ width: '90px' }}>Kind</TableHeaderCell>
                  <TableHeaderCell style={{ width: '110px' }} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {collapse
                  ? groups.flatMap((g) => {
                      const open = openGroups.has(g.key)
                      const rows = [
                        <TableRow key={g.key}>
                          <TableCell>
                            <Button
                              appearance="transparent"
                              size="small"
                              className={s.toggle}
                              icon={open ? <ChevronDownRegular /> : <ChevronRightRegular />}
                              aria-label={open ? 'Hide Occurrences' : 'Show Occurrences'}
                              aria-expanded={open}
                              onClick={() => toggleGroup(g.key)}
                            />
                          </TableCell>
                          <TableCell>{when(g.last)}</TableCell>
                          <TableCell className={s.count}>{g.count}</TableCell>
                          <TableCell className={s.mono}>{g.function}</TableCell>
                          <TableCell className={s.mono}>{g.event}</TableCell>
                          <TableCell className={s.mono}>{g.error_class ?? ''}</TableCell>
                          <TableCell>
                            <Badge appearance="tint" color={g.level === 'error' ? 'danger' : 'warning'}>
                              {KIND_LABEL[g.kind]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button size="small" appearance="subtle" icon={<SpeakerMuteRegular />} onClick={() => mute(g.event)}>
                              Mute Event
                            </Button>
                          </TableCell>
                        </TableRow>,
                      ]
                      if (open) {
                        rows.push(
                          <TableRow key={`${g.key}:detail`} className={s.detailRow}>
                            <TableCell />
                            <TableCell colSpan={7}>
                              <Caption1>
                                {g.count === 1 ? 'Once' : `${g.count} times`}, {when(g.first)} to {when(g.last)}
                              </Caption1>
                              <div className={s.detailList}>
                                {g.rows.map((r, i) => (
                                  <RowDetail key={`${r.at}:${i}`} row={r} mono={s.mono} />
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>,
                        )
                      }
                      return rows
                    })
                  : visibleRows.map((r, i) => (
                      <TableRow key={`${r.at}:${i}`}>
                        <TableCell />
                        <TableCell>{when(r.at)}</TableCell>
                        <TableCell className={s.mono}>{r.function}</TableCell>
                        <TableCell className={s.mono}>{r.event}</TableCell>
                        <TableCell className={s.mono}>{[r.error_class, idsOf(r)].filter(Boolean).join(' · ')}</TableCell>
                        <TableCell>
                          <Badge appearance="tint" color={r.level === 'error' ? 'danger' : 'warning'}>
                            {KIND_LABEL[r.kind]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="small" appearance="subtle" icon={<SpeakerMuteRegular />} onClick={() => mute(r.event)}>
                            Mute Event
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                {visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={collapse ? 8 : 7}>
                      <Body1>Nothing in this window{muted.size > 0 ? ' that is not muted' : ''}.</Body1>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

        </>
      )}

      {view === 'deploys' && (
        <>
          <ScreenIntro className={s.intro}>Each build's report, as it was mailed. Newest first.</ScreenIntro>
          {deploys.status === 'failed' && <ErrorBanner message={deploys.message} />}
          {deploys.status === 'loading' && (
            <div className={s.status} role="status">
              <Spinner size="tiny" />
              <Caption1>Reading the deploy reports…</Caption1>
            </div>
          )}
          {deploys.status === 'ready' && (
            <Table size="small" aria-label="Deploys" className={s.table}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell style={{ width: '32px' }} />
                  <TableHeaderCell style={{ width: '170px' }}>When</TableHeaderCell>
                  <TableHeaderCell style={{ width: '110px' }}>Outcome</TableHeaderCell>
                  <TableHeaderCell>Commit</TableHeaderCell>
                  <TableHeaderCell style={{ width: '120px' }}>Failed Step</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deploys.data.map((d) => {
                  const open = openDeploys.has(d.build_id)
                  return [
                    <TableRow key={d.build_id}>
                      <TableCell>
                        <Button
                          appearance="transparent"
                          size="small"
                          className={s.toggle}
                          icon={open ? <ChevronDownRegular /> : <ChevronRightRegular />}
                          aria-label={open ? 'Hide Report' : 'Show Report'}
                          aria-expanded={open}
                          onClick={() => toggleDeploy(d.build_id)}
                        />
                      </TableCell>
                      <TableCell>{when(d.at)}</TableCell>
                      <TableCell>
                        <Badge appearance="tint" color={d.status === 'SUCCEEDED' ? 'success' : 'danger'}>
                          {d.status === 'SUCCEEDED' ? 'Succeeded' : 'Failed'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={mergeClasses(s.mono)}>{d.sha.slice(0, 8)}</span> {d.subject}
                      </TableCell>
                      <TableCell className={s.mono}>{d.failed_step ?? ''}</TableCell>
                    </TableRow>,
                    open ? (
                      <TableRow key={`${d.build_id}:report`} className={s.detailRow}>
                        <TableCell />
                        <TableCell colSpan={4}>
                          <pre className={s.report}>{d.body}</pre>
                        </TableCell>
                      </TableRow>
                    ) : null,
                  ]
                })}
                {deploys.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Body1>No deploy reports yet. The pipeline writes one per build from 2026-09-14 on.</Body1>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  )
}

/** The identifying fields a row carries, `name=value`, in a fixed order. */
function idsOf(r: OpsErrorRow): string {
  const parts: string[] = []
  for (const key of ['upload_id', 'invoice_id', 'message_id', 'export_id', 'request_id', 'route', 'model_id'] as const) {
    const v = r[key]
    if (v !== undefined) parts.push(`${key}=${v}`)
  }
  if (r.http_status !== undefined) parts.push(`http_status=${r.http_status}`)
  if (r.attempt !== undefined) parts.push(`attempt=${r.attempt}`)
  return parts.join(' ')
}

function RowDetail({ row, mono }: { row: OpsErrorRow; mono: string }) {
  return (
    <>
      <span className={mono}>{when(row.at)}</span>
      <span className={mono}>{[row.reason, idsOf(row)].filter(Boolean).join(' · ') || '—'}</span>
    </>
  )
}
